import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  itemsPerPage?: number;
  totalItems?: number;
  className?: string;
  'data-id'?: string;
}
export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage,
  totalItems,
  className = '',
  'data-id': dataId
}: PaginationProps) {
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;
  // Generate page numbers with ellipsis for large page counts
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 7;
    if (totalPages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      if (currentPage > 3) {
        pages.push('ellipsis-start');
      }
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) {
        pages.push('ellipsis-end');
      }
      // Always show last page
      pages.push(totalPages);
    }
    return pages;
  };
  const pageNumbers = getPageNumbers();
  // Calculate page count text
  const getPageCountText = () => {
    if (!itemsPerPage || !totalItems) return null;
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalItems);
    return `Showing ${start}-${end} of ${totalItems}`;
  };
  return <div className={`flex items-center justify-between ${className}`} data-id="element-3351">
      {/* Page count text */}
      {getPageCountText() && <div className="text-[13px] text-[#64748B]" data-id="element-3352">{getPageCountText()}</div>}

      {/* Pagination controls */}
      <nav className="flex items-center gap-1" aria-label="Pagination" data-id="element-3353">
        {/* Previous button */}
        <button onClick={() => canGoPrevious && onPageChange(currentPage - 1)} disabled={!canGoPrevious} className="flex items-center justify-center w-9 h-9 text-[13px] font-medium text-[#475569] bg-white border border-[#CBD5E1] rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#F1F5F9] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20" aria-label="Go to previous page" data-id="element-3354">
          <ChevronLeftIcon className="h-4 w-4" data-id="element-3355" />
        </button>

        {/* Page numbers */}
        <div className="flex items-center gap-1" role="list" data-id="element-3356">
          {pageNumbers.map((page, index) => {
          if (typeof page === 'string') {
            return <span key={`${page}-${index}`} className="w-9 h-9 flex items-center justify-center text-[13px] font-medium text-[#94A3B8]" aria-hidden="true" data-id="element-3357">
                  ...
                </span>;
          }
          return <button key={page} onClick={() => onPageChange(page)} className={`
                  w-9 h-9 text-[13px] font-medium rounded-md transition-colors duration-200
                  focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20
                  ${page === currentPage ? 'bg-[#0EA5E9] text-white hover:bg-[#0284C7]' : 'bg-white border border-[#CBD5E1] text-[#475569] hover:bg-[#F1F5F9]'}
                `} aria-label={`Go to page ${page}`} aria-current={page === currentPage ? 'page' : undefined} data-id="element-3358">
                {page}
              </button>;
        })}
        </div>

        {/* Next button */}
        <button onClick={() => canGoNext && onPageChange(currentPage + 1)} disabled={!canGoNext} className="flex items-center justify-center w-9 h-9 text-[13px] font-medium text-[#475569] bg-white border border-[#CBD5E1] rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#F1F5F9] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20" aria-label="Go to next page" data-id="element-3359">
          <ChevronRightIcon className="h-4 w-4" data-id="element-3360" />
        </button>
      </nav>
    </div>;
}